import { range, max, isEmpty, orderBy, cloneDeep, trim, toLower } from 'lodash'
import { HARD_SPACE } from 'helpers/utils'

const allowInput = (formQuestion) => !['Hyperlink', 'Paragraph'].includes(formQuestion.questionType)

export const allowHighlight = (formQuestion) =>
  allowInput(formQuestion) &&
  formQuestion.allowHighlight &&
  !['Checkbox', 'SingleSelection', 'MultipleSelection'].includes(formQuestion.questionType)

export const allowAttachment = (formQuestion) => allowInput(formQuestion) && formQuestion.allowAttachment

export const allowRequired = (formQuestion) =>
  allowInput(formQuestion) && formQuestion.questionType !== 'MultipleSelection'

export const getSectionProps = (template, section) => ({
  title: section.name,
  bordered: section.showSectionBorder,
  headStyle: {
    color: section.headerForegroundColor,
    backgroundColor: section.headerBackgroundColor,
    fontFamily: section.headerFontName,
    fontSize: Math.max(section.headerFontSize, 8),
    fontWeight: section.boldHeader ? 600 : 'normal',
    fontStyle: section.italicHeader ? 'italic' : 'normal',
    textDecoration: section.underlineHeader ? 'underline' : 'none',
  },
  bodyStyle: {
    color: section.overrideFormColor ? section.contentForegroundColor : template.formForegroundColor,
    backgroundColor: section.overrideFormColor
      ? section.contentBackgroundColor
      : template.formBackgroundColor,
  },
})

const getFieldWidth = ({ formQuestion: { fieldWidthType } }) => {
  switch (fieldWidthType) {
    case 'OneQuarter':
      return 24 / 4
    case 'OneThird':
      return 24 / 3
    case 'OneHalf':
      return 24 / 2
    case 'TwoThirds':
      return (24 / 3) * 2
    case 'ThreeQuarters':
      return (24 / 4) * 3
    default:
      return 24
  }
}

const getLabelWidth = ({
  formTemplate,
  formQuestion: { questionType, labelWidthType, labelPlacementType },
}) => {
  if (!allowInput({ questionType })) {
    return 0
  }

  if (labelPlacementType === 'Default') {
    return 24
  }

  if (labelPlacementType === 'BasedOnForm' && formTemplate.labelPlacementType === 'Default') {
    return 24
  }

  switch (labelWidthType) {
    case 'OneQuarter':
      return 24 / 4
    case 'OneThird':
      return 24 / 3
    case 'OneHalf':
      return 24 / 2
    case 'TwoThirds':
      return (24 / 3) * 2
    case 'ThreeQuarters':
      return (24 / 4) * 3
    default:
      return 24
  }
}

const getInputWidth = ({
  formTemplate,
  formQuestion: { questionType, labelWidthType, labelPlacementType },
}) => {
  if (!allowInput({ questionType })) {
    return 24
  }

  if (labelPlacementType === 'Default') {
    return 24
  }

  if (labelPlacementType === 'BasedOnForm' && formTemplate.labelPlacementType === 'Default') {
    return 24
  }

  switch (labelWidthType) {
    case 'OneQuarter':
      return (24 / 4) * 3
    case 'OneThird':
      return (24 / 3) * 2
    case 'OneHalf':
      return 24 / 2
    case 'TwoThirds':
      return 24 / 3
    case 'ThreeQuarters':
      return 24 / 4
    default:
      return 24
  }
}

export const getWidths = (params) => ({
  fieldWidth: getFieldWidth(params),
  labelWidth: getLabelWidth(params),
  inputWidth: getInputWidth(params),
})

export const getTotalWidth = (questions = []) =>
  questions.map((each) => getFieldWidth({ formQuestion: each })).reduce((acc, each) => acc + each, 0)

export const sortFormQuestions = (unsorted, injected) => {
  const formQuestions = cloneDeep(orderBy(unsorted, ['line', 'position']))
  const numberOfLines = max([...formQuestions.map((each) => each.line), injected?.line ?? 1])

  let grid = []

  range(numberOfLines + 3).forEach((row) => {
    grid.push(
      formQuestions
        .filter((each) => each.line === row)
        .map((formQuestion, questionIndex) => ({
          ...formQuestion,
          position: questionIndex + 1,
        }))
    )
  })

  if (injected) {
    grid[injected.line].splice(injected.position - 1, 0, injected)
  }

  grid = grid.filter((each) => !isEmpty(each)).map((row) => row.filter((each) => !isEmpty(each)))

  grid.forEach((row, rowIndex) => {
    row.forEach((col, colIndex) => {
      col.line = rowIndex + 1
      col.position = colIndex + 1
    })
  })

  if (injected && grid.some((row) => row.some((col) => col.id === injected.id) && getTotalWidth(row) > 24)) {
    throw new Error('formSectionOverflowError')
  }

  return grid.flat().filter((each) => !isEmpty(each))
}

export const getLabel = ({ formQuestion, labelWidth, color }) =>
  !allowInput(formQuestion) ? null : formQuestion.questionType === 'Checkbox' && labelWidth === 24 ? (
    HARD_SPACE
  ) : (
    <span style={{ color }}>{formQuestion.text}</span>
  )

export const createQuestion = (params) => ({
  sectionId: 0,
  text: '',
  questionType: 'Textbox',
  questionOption: 'http://',
  position: 1,
  line: 1,
  fieldWidthType: 'OneThird',
  required: false,
  labelPlacementType: 'BasedOnForm',
  labelWidthType: 'OneHalf',
  placeholderText: '',
  defaultText: '',
  defaultNumber: null,
  defaultBoolean: false,
  minValue: null,
  maxValue: null,
  decimalPlaces: 0,
  height: 5,
  allowHighlight: false,
  allowAttachment: false,
  overrideSectionColor: false,
  foregroundColor: '#333333',
  backgroundColor: '#FFFFFF',
  fontName: '',
  fontSize: 14,
  bold: false,
  italic: false,
  underline: false,
  formQuestionOptions: [],
  ...params,
})

export const isValidAnswer = (formQuestion, formAnswer) => {
  const text = toLower(trim(formAnswer?.value ?? ''))

  if (allowRequired(formQuestion) && formQuestion.required) {
    if (formQuestion.questionType === 'Checkbox') {
      return text === 'true'
    }

    return text !== ''
  }

  return true
}

export const extractFormQuestions = (formTemplate) =>
  formTemplate.formPages.flatMap((each) => each.formPageSections).flatMap((each) => each.formQuestions)
